import React, { useContext, useEffect, useMemo, useState } from "react";
import SalaryDetail from "../../FinancialReport/Salary/SalaryDetail/SalaryDetail";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getCompanySalaryApprovalDetailV2,
  getSalaryApprovalDetailV2,
  patchCompanySalaryApprovalV2,
  patchSalaryApprovalStepV2,
  pushPayrollApprovalToFinansist,
  TIMESHEET_STATUSES,
} from "../../../api/timesheetApi";
import "./SalaryApprovalDetail.css";

/** false — реальная запись в Finansist; true — только проверка (dry_run), без создания документов. */
const FINANSIST_PUSH_DRY_RUN = false;

const formatPersonShortName = (person) => {
  if (!person) return "-";
  const surname = person.surname || "";
  const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
  return `${surname} ${firstInitial}`.trim() || "-";
};

const normalizeStatus = (status) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
    case "согласован":
      return "Согласован";
    case "rejected":
    case "declined":
    case "отклонено":
    case "не согласован":
      return "Отклонено";
    case "pending":
    case "на рассмотрении":
    default:
      return "На рассмотрении";
  }
};

const getLevelName = (level) => {
  if (typeof level === "string") return level;
  return level?.name || level?.access_level_name || level?.title || "";
};

/** Карта user_id → ФИО из ответа salary-approval-v2 (таблица согласования). */
const buildUserIdToNameFromApprovalDetail = (detail) => {
  const map = new Map();
  const groups = detail?.table_data?.groups || [];
  for (const group of groups) {
    for (const row of group.rows || []) {
      const id = Number(row?.user_id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const name = String(row?.full_name || "").trim();
      map.set(id, name || `Пользователь (id: ${id})`);
    }
  }
  return map;
};

const formatFinansistPushResultMessage = (result, userIdToName) => {
  const usersOk = Number(result?.users_ok || 0);
  const usersFailed = Number(result?.users_failed || 0);
  const lines = [
    "Отправка в Finansist выполнена.",
    `Успешно: ${usersOk}`,
    `С ошибками: ${usersFailed}`,
  ];

  const failed = (
    Array.isArray(result?.per_user) ? result.per_user : []
  ).filter((u) => u && u.ok === false);
  if (failed.length === 0) {
    return lines.join("\n");
  }

  lines.push("");
  lines.push("Детали по ошибкам:");
  for (const entry of failed) {
    const uid = Number(entry.user_id);
    const who =
      Number.isFinite(uid) && uid > 0
        ? userIdToName?.get(uid) || `Пользователь (id: ${uid})`
        : "Неизвестный пользователь";
    const detail =
      String(entry.detail || "").trim() ||
      String(entry.empty_reason_hint || "").trim() ||
      "Причина не указана.";
    lines.push(`• ${who}: ${detail}`);
    const unresolved = entry.unresolved_projects;
    if (Array.isArray(unresolved) && unresolved.length) {
      lines.push(`  Неразрешённые проекты: ${unresolved.join(", ")}`);
    }
  }
  return lines.join("\n");
};

const CompanyApprovalDetail = ({ approvalId, department, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!approvalId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getCompanySalaryApprovalDetailV2(approvalId);
        setDetail(data);
      } catch (e) {
        console.error(e);
        setError(e.message || "Ошибка при загрузке");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [approvalId]);

  const approvalStatus = normalizeStatus(
    detail?.approval?.status || department?.status,
  );
  const canAct = approvalStatus === "На рассмотрении";
  const rows = detail?.table_data?.rows || [];

  // Серверные поля `detail.total_*` иногда приходят как `0`, хотя суммы по строкам
  // отделений корректные. Поэтому считаем итоги локально по `rows`, чтобы UI
  // всегда отражал реальные значения таблицы.
  const computedCompanyTotals = useMemo(() => {
    const totalSalary = rows.reduce(
      (sum, row) => sum + Number(row?.total_salary || 0),
      0,
    );
    const totalPaid = rows.reduce(
      (sum, row) => sum + Number(row?.paid || 0),
      0,
    );
    const totalRemainder = rows.reduce(
      (sum, row) => sum + Number(row?.remainder || 0),
      0,
    );
    return { totalSalary, totalPaid, totalRemainder };
  }, [rows]);

  const periodLabel = useMemo(() => {
    const year = detail?.table_data?.year ?? detail?.approval?.year;
    const month = detail?.table_data?.month ?? detail?.approval?.month;
    if (!year || !month) return "-";
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[Math.max(0, Number(month) - 1)]} ${year}`;
  }, [
    detail?.approval?.month,
    detail?.approval?.year,
    detail?.table_data?.month,
    detail?.table_data?.year,
  ]);

  const submitDecision = async (status, comment = "") => {
    if (!approvalId) return;
    setIsSubmitting(true);
    try {
      await patchCompanySalaryApprovalV2(approvalId, { status, comment });
      if (onBack) await onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось обновить статус согласования.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading)
    return <div className="salary-approval-detail__loading">Загрузка...</div>;
  if (error)
    return (
      <div className="salary-approval-detail__loading">Ошибка: {error}</div>
    );
  if (!detail)
    return <div className="salary-approval-detail__loading">Нет данных</div>;

  return (
    <div className="salary-detail">
      <div className="salary-detail__header">
        <button className="salary-detail__back-button" onClick={onBack}>
          <span className="salary-detail__back-arrow">←</span>
          Согласование зарплаты
        </button>
      </div>

      <div className="salary-detail__summary">
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Период:</span>
          <span className="salary-detail__summary-value">{periodLabel}</span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">
            Общая сумма к выплате:
          </span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalSalary || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Выплачено:</span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalPaid || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Остаток:</span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalRemainder || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Отправил:</span>
          <span className="salary-detail__summary-value">
            {formatPersonShortName(detail?.approval?.initiator)}
          </span>
        </div>
      </div>

      <div className="salary-detail__table-container">
        <table className="salary-detail__table salary-detail__table--collapsed-sections">
          <thead>
            <tr>
              <th>Отделение</th>
              <th>Количество сотрудников</th>
              <th>Заработная плата</th>
              <th>Выплачено</th>
              <th>Остаток</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isDeptOpen = Boolean(
                expandedDepartments[row.department_id],
              );
              return (
                <React.Fragment key={row.department_id}>
                  <tr className="salary-detail__table-row salary-detail__group-row">
                    <td className="salary-detail__group-cell">
                      <button
                        type="button"
                        className="salary-detail__group-toggle"
                        onClick={() =>
                          setExpandedDepartments((prev) => ({
                            ...prev,
                            [row.department_id]: !prev[row.department_id],
                          }))
                        }
                      >
                        <span className="salary-detail__group-chevron">
                          {isDeptOpen ? "▼" : "▶"}
                        </span>
                        <span className="salary-detail__group-title">
                          {row.department_name}
                        </span>
                      </button>
                    </td>
                    <td>{row.employees_count || 0}</td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(
                        row.total_salary || 0,
                      )}{" "}
                      тг
                    </td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(row.paid || 0)} тг
                    </td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(
                        row.remainder || 0,
                      )}{" "}
                      тг
                    </td>
                  </tr>
                  {isDeptOpen &&
                    (row.groups || []).map((group) => {
                      const groupKey = `${row.department_id}-${group.group_id}`;
                      const isGroupOpen = Boolean(expandedGroups[groupKey]);
                      return (
                        <React.Fragment key={groupKey}>
                          <tr className="salary-detail__table-row">
                            <td style={{ paddingLeft: "28px" }}>
                              <button
                                type="button"
                                className="salary-detail__group-toggle"
                                onClick={() =>
                                  setExpandedGroups((prev) => ({
                                    ...prev,
                                    [groupKey]: !prev[groupKey],
                                  }))
                                }
                              >
                                <span className="salary-detail__group-chevron">
                                  {isGroupOpen ? "▼" : "▶"}
                                </span>
                                <span className="salary-detail__group-title">
                                  {group.group_name}
                                </span>
                              </button>
                            </td>
                            <td>{group.employees_count || 0}</td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.total_salary || 0,
                              )}{" "}
                              тг
                            </td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.paid || 0,
                              )}{" "}
                              тг
                            </td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.remainder || 0,
                              )}{" "}
                              тг
                            </td>
                          </tr>
                          {isGroupOpen &&
                            (group.employees || []).map((employee) => (
                              <tr
                                key={`${groupKey}-${employee.user_id}`}
                                className="salary-detail__table-row salary-detail__employee-row"
                              >
                                <td style={{ paddingLeft: "56px" }}>
                                  {employee.full_name}
                                </td>
                                <td></td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.total_salary || 0,
                                  )}{" "}
                                  тг
                                </td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.paid || 0,
                                  )}{" "}
                                  тг
                                </td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.remainder || 0,
                                  )}{" "}
                                  тг
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
            <tr className="salary-detail__table-row salary-detail__table-row--total">
              <td className="salary-detail__total-label">ИТОГО</td>
              <td></td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalSalary || 0,
                )}{" "}
                тг
              </td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalPaid || 0,
                )}{" "}
                тг
              </td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalRemainder || 0,
                )}{" "}
                тг
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {canAct && (
        <div className="salary-detail__actions">
          <button
            className="salary-detail__button salary-detail__button--reset"
            onClick={() => setShowRejectModal(true)}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#fff",
              color: "#ff4d4f",
              border: "1px solid #ff4d4f",
            }}
          >
            Отклонить
          </button>
          <button
            className="salary-detail__button salary-detail__button--submit"
            onClick={() => setShowConfirmApprove(true)}
            disabled={isSubmitting}
          >
            Согласовать
          </button>
        </div>
      )}

      {showConfirmApprove && (
        <div
          className="popup-overlay"
          onClick={() => setShowConfirmApprove(false)}
        >
          <div
            className="popup popup-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="popup-text">
              Подтвердить согласование зарплаты компании?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowConfirmApprove(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={() => submitDecision(TIMESHEET_STATUSES.APPROVED)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Согласовать"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="popup-overlay"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">
              Отклонение зарплаты компании
            </h3>
            <div className="popup-reject__comment-section">
              <label className="popup-reject__label">
                Комментарий <span className="popup-reject__required">*</span>
              </label>
              <textarea
                className={`popup-reject__textarea ${
                  rejectCommentError ? "popup-reject__textarea--error" : ""
                }`}
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  if (rejectCommentError) setRejectCommentError("");
                }}
                rows={4}
              />
              {rejectCommentError && (
                <span className="popup-reject__error">
                  {rejectCommentError}
                </span>
              )}
            </div>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={() => {
                  if (!rejectComment.trim()) {
                    setRejectCommentError(
                      "Комментарий обязателен для заполнения",
                    );
                    return;
                  }
                  submitDecision(
                    TIMESHEET_STATUSES.REJECTED,
                    rejectComment.trim(),
                  );
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Отклонить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SalaryApprovalDetail = ({ department, onBack }) => {
  const { userData } = useContext(UserContext);
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPushingToFinansist, setIsPushingToFinansist] = useState(false);
  const [showFinansistModal, setShowFinansistModal] = useState(false);
  const [showFinansistResultModal, setShowFinansistResultModal] =
    useState(false);
  const [finansistResultText, setFinansistResultText] = useState("");
  const [finansistResultVariant, setFinansistResultVariant] =
    useState("success");

  const currentUserPendingStep = useMemo(() => {
    if (!userData?.id || !department?.approvers?.length) return null;
    return department.approvers.find(
      (a) =>
        Number(a.approverId) === Number(userData.id) &&
        a.status === "На рассмотрении",
    );
  }, [department?.approvers, userData?.id]);

  const isApprovedSalary = useMemo(
    () => normalizeStatus(department?.status) === TIMESHEET_STATUSES.APPROVED,
    [department?.status],
  );

  const canPushToFinansist = useMemo(() => {
    const accessLevels = userData?.access_levels || [];
    const hasRequiredRole = accessLevels.some((level) => {
      const levelName = getLevelName(level);
      return ["АДМС", "ФИН"].includes(levelName);
    });

    return (
      hasRequiredRole && isApprovedSalary && Boolean(department?.approvalId)
    );
  }, [department?.approvalId, isApprovedSalary, userData?.access_levels]);

  const openFinansistModal = () => {
    setShowFinansistModal(true);
  };

  const closeFinansistResultModal = () => {
    setShowFinansistResultModal(false);
    setFinansistResultText("");
  };

  const handlePushToFinansist = async () => {
    if (!department?.approvalId) {
      setFinansistResultVariant("error");
      setFinansistResultText("Не найден approval_id для отправки в Finansist.");
      setShowFinansistResultModal(true);
      return;
    }

    setIsPushingToFinansist(true);
    try {
      const result = await pushPayrollApprovalToFinansist({
        approval_id: department.approvalId,
        dry_run: FINANSIST_PUSH_DRY_RUN,
        on_missing_contragent: "skip",
        include_documents_in_response: true,
      });
      const hasPerUserFailures = (
        Array.isArray(result?.per_user) ? result.per_user : []
      ).some((u) => u && u.ok === false);
      let userIdToName = new Map();
      if (hasPerUserFailures) {
        try {
          const approvalDetail = await getSalaryApprovalDetailV2(
            department.approvalId,
          );
          userIdToName = buildUserIdToNameFromApprovalDetail(approvalDetail);
        } catch (e) {
          console.error(e);
        }
      }
      setFinansistResultVariant("success");
      setFinansistResultText(
        formatFinansistPushResultMessage(result, userIdToName),
      );
      setShowFinansistModal(false);
      setShowFinansistResultModal(true);
    } catch (e) {
      console.error(e);
      setFinansistResultVariant("error");
      setFinansistResultText(
        e?.message || "Не удалось отправить зарплату в Finansist.",
      );
      setShowFinansistModal(false);
      setShowFinansistResultModal(true);
    } finally {
      setIsPushingToFinansist(false);
    }
  };

  const handleConfirmApprove = async () => {
    const stepId = currentUserPendingStep?.stepId;
    if (!stepId) {
      window.alert("Не найден шаг согласования для текущего пользователя.");
      return;
    }
    setIsSubmitting(true);
    try {
      await patchSalaryApprovalStepV2(stepId, {
        status: TIMESHEET_STATUSES.APPROVED,
        comment: "",
      });
      setShowConfirmApprove(false);
      if (onBack) onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось согласовать. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectComment.trim()) {
      setRejectCommentError("Комментарий обязателен для заполнения");
      return;
    }
    const stepId = currentUserPendingStep?.stepId;
    if (!stepId) {
      window.alert("Не найден шаг согласования для текущего пользователя.");
      return;
    }
    setIsSubmitting(true);
    try {
      await patchSalaryApprovalStepV2(stepId, {
        status: TIMESHEET_STATUSES.REJECTED,
        comment: rejectComment.trim(),
      });
      setShowRejectModal(false);
      setRejectComment("");
      setRejectCommentError("");
      if (onBack) onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось отклонить. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (department?.isCompanyApproval) {
    return (
      <CompanyApprovalDetail
        approvalId={department.approvalId}
        department={department}
        onBack={onBack}
      />
    );
  }

  return (
    <div>
      <SalaryDetail
        department={department}
        onBack={onBack}
        initialMonth={department?.date}
        readOnlyMode
        approvalId={department?.approvalId}
        backButtonLabel="Согласование зарплаты"
        customActions={
          currentUserPendingStep || canPushToFinansist ? (
            <>
              {currentUserPendingStep && (
                <>
                  <button
                    className="salary-detail__button salary-detail__button--reset"
                    onClick={() => setShowRejectModal(true)}
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: "#fff",
                      color: "#ff4d4f",
                      border: "1px solid #ff4d4f",
                    }}
                  >
                    Отклонить
                  </button>
                  <button
                    className="salary-detail__button salary-detail__button--submit"
                    onClick={() => setShowConfirmApprove(true)}
                    disabled={isSubmitting}
                  >
                    Согласовать
                  </button>
                </>
              )}
              {canPushToFinansist && (
                <button
                  className="salary-detail__button salary-detail__button--submit"
                  onClick={openFinansistModal}
                  disabled={isPushingToFinansist}
                >
                  {isPushingToFinansist
                    ? "Отправка в Finansist..."
                    : "Отправить в Finansist"}
                </button>
              )}
            </>
          ) : null
        }
      />

      {showConfirmApprove && (
        <div
          className="popup-overlay"
          onClick={() => setShowConfirmApprove(false)}
        >
          <div
            className="popup popup-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="popup-text">
              Вы уверены, что хотите согласовать зарплату для отдела "
              {department?.name}"?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowConfirmApprove(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={handleConfirmApprove}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Согласовать"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="popup-overlay"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">Отклонение зарплаты</h3>
            <p className="popup-reject__subtitle">
              Вы уверены, что хотите отклонить зарплату для отдела "
              {department?.name}"?
            </p>
            <div className="popup-reject__comment-section">
              <label className="popup-reject__label">
                Комментарий <span className="popup-reject__required">*</span>
              </label>
              <textarea
                className={`popup-reject__textarea ${
                  rejectCommentError ? "popup-reject__textarea--error" : ""
                }`}
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  if (rejectCommentError) setRejectCommentError("");
                }}
                placeholder="Укажите причину отклонения..."
                rows={4}
              />
              {rejectCommentError && (
                <span className="popup-reject__error">
                  {rejectCommentError}
                </span>
              )}
            </div>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={handleConfirmReject}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Отклонить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinansistModal && (
        <div
          className="popup-overlay"
          onClick={() => setShowFinansistModal(false)}
        >
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">Отправка в Finansist</h3>
            <p className="popup-reject__subtitle">
              {FINANSIST_PUSH_DRY_RUN
                ? "Тестовый прогон (dry_run): документы в Finansist не создаются. Параметры:"
                : "Будет выполнена фактическая отправка в Finansist. Параметры:"}
              <br />
              <code>approval_id={department?.approvalId}</code>,{" "}
              <code>dry_run={FINANSIST_PUSH_DRY_RUN ? "true" : "false"}</code>,{" "}
              <code>on_missing_contragent=skip</code>
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowFinansistModal(false)}
                disabled={isPushingToFinansist}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={handlePushToFinansist}
                disabled={isPushingToFinansist}
              >
                {isPushingToFinansist ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinansistResultModal && (
        <div className="popup-overlay" onClick={closeFinansistResultModal}>
          <div
            className="popup popup-finansist-result"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className={`popup-finansist-result__title ${
                finansistResultVariant === "error"
                  ? "popup-finansist-result__title--error"
                  : ""
              }`}
            >
              {finansistResultVariant === "error"
                ? "Ошибка отправки в Finansist"
                : "Результат отправки в Finansist"}
            </h3>
            <pre className="popup-finansist-result__body">
              {finansistResultText}
            </pre>
            <div className="popup-buttons">
              <button
                type="button"
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={closeFinansistResultModal}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryApprovalDetail;
